"use client"

import { useForm } from "react-hook-form"
import { Form, FormControl, FormField, FormItem, FormMessage } from "../../components/ui/form"
import { Input } from "../../components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "../../components/ui/button"
import BackIcon from "../../components/back-icon"
import { addPatientSchema, type AddPatientSchema } from "../../lib/validation/add-patient"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover"
import { cn } from "../../lib/utils"
import { CalendarIcon, Loader2 } from "lucide-react"
import { format } from "date-fns"
import { Calendar } from "../../components/ui/calendar"
import { useAddPatient } from "../../lib/hooks/patient/use-add-patient"
import { useAppointmentData } from "../../contexts/appointment-data-context"
import { useAuth } from "../../contexts/auth-context"
import { useState, useEffect } from "react"
import Logo from "../../components/logo"
import { calculateBMI } from "../../lib/utils"

function AddPatient() {
  const [searchParams] = useSearchParams()
  const backUrl = searchParams.get("backUrl") ?? "/doctors"
  const redirectUrl = searchParams.get("redirectUrl")
  const navigate = useNavigate()
  const form = useForm<AddPatientSchema>({
    resolver: zodResolver(addPatientSchema),
  })

  const [date, setDate] = useState<Date | null>(null)
  const [dobOrAgeErr, setDobOrAgeErr] = useState("")
  const [bmi, setBmi] = useState<string>("")
  const [bmiEdited, setBmiEdited] = useState<boolean>(false)
  const { mobile } = useAuth()
  const { data: apptData, setData } = useAppointmentData()

  const { addPatient, isPending } = useAddPatient()

  function calculateAge(dob: Date) {
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDifference = today.getMonth() - dob.getMonth()
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  function handleAddPatient(data: AddPatientSchema) {
    if (!data.dob && !data.age) return
    addPatient(
      { ...data, mobile: mobile ?? "" },
      {
        onSuccess: (newData) => {
          localStorage.setItem("selectedPatient", newData.name)
          form.reset()
          setData({
            ...apptData,
            patient: {
              id: newData.patientId,
              name: newData.name,
            },
            branchDetails: apptData?.branchDetails || [],
          })
          navigate(`${backUrl}?step=2`)
        },
      },
    )
  }

  useEffect(() => {
    const height = form.watch("height");
    const weight = form.watch("weight");
    if (!height || !weight) {
      setBmi("");
    }
  }, [form.watch("height"), form.watch("weight")])

  return (
    <>
      <nav className="sticky top-0 z-10 flex w-full items-center justify-start gap-4 px-6 py-2 border-b bg-white/95 backdrop-blur-sm shadow-sm">
        <Logo />
      </nav>
      <div className="container flex min-h-[calc(100vh-73px)] max-w-md flex-col py-6 sm:justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="mb-6 flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`${backUrl}`)}
            className="relative group h-10 w-10 rounded-full border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all"
          >
            <BackIcon />
            <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:flex items-center p-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
              Back
            </span>
          </Button>
          <h1 className="text-xl font-bold text-gray-800">Add a Patient</h1>
        </div>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              const values = form.watch()
              if (!values.dob && !values.age) setDobOrAgeErr("Please enter either DOB or Age")

              form.handleSubmit(handleAddPatient)(e)
            }}
            className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder="Patient Name or ABHA Id"
                      {...field}
                      onKeyDown={(e) => {
                        const key = e.key
                        const regex = /^[a-zA-Z\s]*$/
                        if (!regex.test(key)) e.preventDefault()
                      }}
                      disabled={isPending}
                      className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm font-medium text-gray-600 mb-2">Patient Age Information</p>
              <div className="flex items-center gap-3">
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <FormItem className="w-24 shrink-0">
                      <FormControl>
                        <Input
                          placeholder="Age"
                          type="number"
                          {...field}
                          min="0"
                          onChange={(e) => {
                            const val = e.target.value
                            if (val.length > 3 || Number.parseInt(val) < 0) return
                            field.onChange(e)
                            setDobOrAgeErr("")
                            if (!val) setDobOrAgeErr("Please enter either DOB or Age")
                          }}
                          disabled={isPending}
                          className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <span className="text-gray-500 font-medium">Or</span>
                <FormField
                  control={form.control}
                  name="dob"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild disabled={isPending}>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "h-12 w-full text-left font-normal rounded-lg border-gray-200",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
                              {field.value ? format(field.value, "dd MMMM yyyy") : <span>Date of Birth</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="center">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown"
                              selected={date || field.value}
                              onSelect={(selectedDate) => {
                                setDate(selectedDate!)
                                field.onChange(selectedDate)
                                setDobOrAgeErr("")
                                if (selectedDate) {
                                  const calculatedAge = calculateAge(selectedDate)
                                  form.setValue("age", String(calculatedAge))
                                }
                              }}
                              fromYear={new Date().getFullYear() - 120}
                              toDate={new Date()}
                              className="rounded-md border border-gray-200"
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {dobOrAgeErr && <p className="text-sm font-medium text-destructive mt-1">{dobOrAgeErr}</p>}
            </div>

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        disabled={isPending}
                        className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                      >
                        <SelectValue placeholder="Gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                        <SelectItem value="O">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className=" bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex gap-4 p-2 bg-gray-50  border-gray-100">
                <FormField
                  control={form.control}
                  name="height"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Height"
                            type="number"
                            {...field}
                            disabled={isPending}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.length > 3 || Number.parseInt(val) < 0) return;
                              field.onChange(e);
                              if (!bmiEdited) {
                                const weight = form.getValues("weight");
                                if (weight && val) {
                                  setBmi(calculateBMI(Number(val), Number(weight)));
                                } else {
                                  setBmi("");
                                }
                              }
                            }}
                            className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 pr-10"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">cm</span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Weight"
                            type="number"
                            {...field}
                            disabled={isPending}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.length > 3 || Number.parseInt(val) < 0) return;
                              field.onChange(e);
                              if (!bmiEdited) {
                                const height = form.getValues("height");
                                if (height && val) {
                                  setBmi(calculateBMI(Number(height), Number(val)));
                                } else {
                                  setBmi("");
                                }
                              }
                            }}
                            className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 pr-10"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">kg</span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className=" p-2 bg-gray-50  border border-gray-100">
                <div className="relative">
                  <Input
                    placeholder="BMI"
                    value={bmi}
                    readOnly={true}
                    disabled={true}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setBmi(val);
                        setBmiEdited(val !== "");
                      }
                    }}
                    //disabled={isPending}  // <- This is the only disabled condition
                    className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100 pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">kg/m²</span>
                </div>
                {!bmi && (
                  <p className="text-[0.69rem] text-black/70 mt-2">Enter height and weight both to autocalculate BMI</p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-600">Medical History</p>
              <FormField
                control={form.control}
                name="allergies"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Any Allergies"
                        {...field}
                        disabled={isPending}
                        className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pastIllness"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Any Past Illness or Procedures"
                        {...field}
                        disabled={isPending}
                        className="h-12 rounded-lg border-gray-200 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="bottom-5 left-0 w-full space-y-4 max-sm:sticky">
              <Button
                type="submit"
                className="w-full font-bold shadow-xl"
                size="lg"
                disabled={isPending}
              >
                {isPending && (
                  <Loader2
                    className="mr-1 size-4 animate-spin"
                    strokeWidth={2.5}
                  />
                )}
                Continue
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  )
}

export default AddPatient
